'use client';

import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';

type SegmentationLinksProps<T> = {
  namespace: string;
  items: T[];
  selectedId: T;
  onSelect: (id: T) => void;
  className?: string;
} & Omit<ComponentProps<'div'>, 'children' | 'onSelect'>;

function Divider() {
  return <span className="h-4 w-px shrink-0 bg-border mx-4" aria-hidden />;
}

function Button({ isSelected, children, onClick, testId }: { isSelected: boolean; children: React.ReactNode; onClick: () => void; testId?: string }) {
  const classes = {
    base: 'relative cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring after:transition-all after:duration-300 after:absolute after:-bottom-px after:left-0 after:w-full after:h-0.5 after:bg-secondary-foreground after:scale-x-0 after:origin-[0_0]',
    selected: 'text-secondary-foreground link-label after:scale-x-100',
    unselected: 'paragraph hover:text-secondary-foreground hover:after:scale-x-100',
  };
  return (
    <button
      type="button"
      className={twMerge(classes.base, isSelected ? classes.selected : classes.unselected)}
      role="tab"
      aria-selected={isSelected}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/**
 * SegmentationLinks component is a horizontal segmentation control.
 * It displays a list of actionable items with a selected item and unselected items.
 * The selected item is displayed as a bold, underlined, not clickable link.
 * The unselected items are displayed as clickable links.
 * The component accepts a list of items and a selected item id.
 * The component calls the onSelect function when an item is clicked.
 */
function SegmentationLinks<T extends string>({ namespace, items, selectedId, onSelect, className, ...props }: SegmentationLinksProps<T>) {
  const t = useTranslations(namespace);

  const classes = {
    container: twMerge('flex items-center', className),
    item: 'flex items-center',
  };
  return (
    <div className={classes.container} role="tablist" {...props}>
      {items.map((item, i) => (
        <div key={item} className={classes.item}>
          {i > 0 && <Divider />}
          <Button isSelected={item === selectedId} onClick={() => onSelect(item)} testId={`segmentation-link-${item}`}>
            {t(item as string)}
          </Button>
        </div>
      ))}
    </div>
  );
}

export { Button, Divider, SegmentationLinks };
